import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Users, Clock, Mail, CheckCircle, XCircle, 
  LogOut, Loader2, RefreshCw, Eye, Trash2, CreditCard, AlertCircle,
  Gift, Calendar, BarChart3, AlertTriangle, RotateCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import AdminAnalytics from '@/components/AdminAnalytics';
import { 
  adminGetTradies, adminApproveTradie, adminRejectTradie, adminDeleteTradie,
  adminGetEnquiries, adminMarkEnquiryRead, adminGetStats, adminSetFreeSubscription,
  adminSendTrialWarnings, adminGetExpiringTrials, adminGetExpiredCount, adminExtendTrials
} from '@/lib/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout, getToken, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tradies, setTradies] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTradie, setSelectedTradie] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expiredCount, setExpiredCount] = useState(0);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      loadData();
    }
  }, [admin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const [statsData, tradiesData, enquiriesData, expiredData] = await Promise.all([
        adminGetStats(token),
        adminGetTradies(token),
        adminGetEnquiries(token),
        adminGetExpiredCount(token).catch(() => ({ expired_count: 0 })),
      ]);
      setStats(statsData);
      setTradies(tradiesData);
      setEnquiries(enquiriesData);
      setExpiredCount(expiredData.expired_count || 0);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateAll = async (days = 90) => {
    setReactivating(true);
    try {
      const token = getToken();
      const res = await adminExtendTrials(token, days, true);
      const n = res.updated;
      toast.success(`Reactivated ${n} tradie${n === 1 ? '' : 's'}`, {
        description: `Trial extended by ${days} days`
      });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate tradies');
    } finally {
      setReactivating(false);
    }
  };

  const handleApprove = async (tradieId) => {
    setActionLoading(true);
    try {
      await adminApproveTradie(getToken(), tradieId);
      toast.success('Tradie approved successfully');
      loadData();
      setSelectedTradie(null);
    } catch (error) {
      toast.error('Failed to approve tradie');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (tradieId) => {
    setActionLoading(true);
    try {
      await adminRejectTradie(getToken(), tradieId);
      toast.success('Tradie rejected');
      loadData();
      setSelectedTradie(null);
    } catch (error) {
      toast.error('Failed to reject tradie');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (tradieId) => {
    setActionLoading(true);
    try {
      await adminDeleteTradie(getToken(), tradieId);
      toast.success('Tradie deleted');
      loadData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Failed to delete tradie');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkEnquiryRead = async (enquiryId) => {
    try {
      await adminMarkEnquiryRead(getToken(), enquiryId);
      loadData();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleToggleFree = async (tradieId, currentIsFree) => {
    setActionLoading(true);
    try {
      await adminSetFreeSubscription(getToken(), tradieId, !currentIsFree);
      toast.success(currentIsFree ? 'Free subscription removed' : 'Free subscription enabled');
      loadData();
      setSelectedTradie(null);
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTrialWarnings = async () => {
    setActionLoading(true);
    try {
      const result = await adminSendTrialWarnings(getToken());
      if (result.emails_sent > 0) {
        toast.success(`Sent ${result.emails_sent} trial expiry warning emails`, {
          description: result.tradies_warned.map(t => t.business_name).join(', ')
        });
      } else {
        toast.info('No trial warnings to send', {
          description: 'All tradies with expiring trials have already been notified'
        });
      }
      loadData();
    } catch (error) {
      toast.error('Failed to send trial warnings');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSubscriptionBadge = (tradie) => {
    if (tradie.is_free) {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Free</Badge>;
    }
    if (tradie.trial_ends_at) {
      const trialEnd = new Date(tradie.trial_ends_at);
      if (trialEnd > new Date()) {
        const daysLeft = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Trial ({daysLeft}d)</Badge>;
      }
    }
    if (tradie.subscription_status === 'active') {
      return <Badge className="bg-primary/10 text-primary border-primary/20">Active</Badge>;
    }
    return <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingTradies = tradies.filter(t => t.status === 'pending');
  const approvedTradies = tradies.filter(t => t.status === 'approved');
  const newEnquiries = enquiries.filter(e => e.status === 'new');

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <span className="font-heading font-bold text-lg">Admin Dashboard</span>
                <span className="block text-xs text-muted-foreground -mt-0.5">{admin?.email}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Expired-trial alert banner */}
        {expiredCount > 0 && (
          <div
            className="mb-6 rounded-lg border-2 border-secondary/40 bg-secondary/5 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            data-testid="expired-trials-banner"
          >
            <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-secondary" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-foreground">
                {expiredCount} tradie{expiredCount === 1 ? '' : 's'} hidden from the directory
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Their trial has expired and they haven't subscribed yet. You can extend everyone's trial with one click.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="bg-secondary hover:bg-secondary/90 text-white flex-shrink-0"
                  disabled={reactivating}
                  data-testid="reactivate-all-btn"
                >
                  {reactivating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reactivating...</>
                  ) : (
                    <><RotateCw className="w-4 h-4 mr-2" />Reactivate all</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reactivate {expiredCount} tradie{expiredCount === 1 ? '' : 's'}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will extend the trial by <strong>90 days</strong> for every approved tradie whose trial has expired and who hasn't subscribed. They'll immediately re-appear on the public directory. Tradies with active paid subscriptions and free listings are left unchanged.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleReactivateAll(90)}>
                    Reactivate for 90 days
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({pendingTradies.length})
            </TabsTrigger>
            <TabsTrigger value="tradies" data-testid="tab-tradies">
              <Users className="w-4 h-4 mr-2" />
              All Tradies
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              <Mail className="w-4 h-4 mr-2" />
              Enquiries ({newEnquiries.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Tradies</CardDescription>
                  <CardTitle className="text-3xl font-heading">{stats?.total_tradies || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Users className="w-8 h-8 text-primary/30" />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Pending Approval</CardDescription>
                  <CardTitle className="text-3xl font-heading text-amber-600">{stats?.pending_tradies || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Clock className="w-8 h-8 text-amber-200" />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>On Trial</CardDescription>
                  <CardTitle className="text-3xl font-heading text-blue-600">{stats?.on_trial || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar className="w-8 h-8 text-blue-200" />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Active Subscriptions</CardDescription>
                  <CardTitle className="text-3xl font-heading text-emerald-600">{stats?.active_subscriptions || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CreditCard className="w-8 h-8 text-emerald-200" />
                </CardContent>
              </Card>
            </div>

            {/* Trial Warnings Section */}
            {stats?.on_trial > 0 && (
              <Card className="mb-8">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Trial Expiry Notifications
                    </CardTitle>
                    <CardDescription>
                      {stats?.on_trial} tradies currently on free trial
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={handleSendTrialWarnings}
                    disabled={actionLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="send-trial-warnings-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Send Expiry Warnings
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Click "Send Expiry Warnings" to email tradies whose trials expire within 7 days. 
                    Each tradie receives only one warning email.
                  </p>
                </CardContent>
              </Card>
            )}

            {pendingTradies.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Pending Approvals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingTradies.slice(0, 5).map((tradie) => (
                      <div 
                        key={tradie.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100"
                      >
                        <div>
                          <p className="font-medium">{tradie.business_name}</p>
                          <p className="text-sm text-muted-foreground">{tradie.email}</p>
                        </div>
                        <Button size="sm" onClick={() => setSelectedTradie(tradie)}>
                          Review
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Pending Tab */}
          <TabsContent value="pending">
            {pendingTradies.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <CheckCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
                  <p className="text-lg font-medium">All caught up!</p>
                  <p className="text-muted-foreground">No pending applications to review.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingTradies.map((tradie) => (
                  <Card key={tradie.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-heading font-semibold text-lg">{tradie.business_name}</h3>
                            {getStatusBadge(tradie.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{tradie.contact_name} • {tradie.email}</p>
                          <p className="text-sm mb-3">{tradie.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {tradie.categories.map(cat => (
                              <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedTradie(tradie)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprove(tradie.id)}
                            disabled={actionLoading}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReject(tradie.id)}
                            disabled={actionLoading}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Tradies Tab */}
          <TabsContent value="tradies">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">All Tradies ({tradies.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {tradies.map((tradie) => (
                      <div 
                        key={tradie.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-stone-200 hover:bg-stone-50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{tradie.business_name}</p>
                            {getStatusBadge(tradie.status)}
                            {getSubscriptionBadge(tradie)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {tradie.contact_name} • {tradie.email} • {tradie.phone}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedTradie(tradie)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteConfirm(tradie)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Enquiries ({enquiries.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {enquiries.length === 0 ? (
                  <div className="py-12 text-center">
                    <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No enquiries yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {enquiries.map((enquiry) => (
                        <div 
                          key={enquiry.id}
                          className={`p-4 rounded-lg border cursor-pointer hover:bg-stone-50 transition-colors ${
                            enquiry.status === 'new' ? 'border-primary bg-primary/5' : 'border-stone-200'
                          }`}
                          onClick={() => {
                            setSelectedEnquiry(enquiry);
                            if (enquiry.status === 'new') {
                              handleMarkEnquiryRead(enquiry.id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium">{enquiry.name}</p>
                              <p className="text-sm text-muted-foreground">{enquiry.email}</p>
                            </div>
                            {enquiry.status === 'new' && (
                              <Badge className="bg-primary text-primary-foreground">New</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            To: <span className="font-medium">{enquiry.tradie_name}</span>
                          </p>
                          <p className="text-sm line-clamp-2">{enquiry.message}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AdminAnalytics token={getToken()} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Tradie Detail Dialog */}
      <Dialog open={!!selectedTradie} onOpenChange={() => setSelectedTradie(null)}>
        <DialogContent className="max-w-2xl">
          {selectedTradie && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{selectedTradie.business_name}</DialogTitle>
                <DialogDescription>
                  {selectedTradie.contact_name} • Registered {new Date(selectedTradie.created_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  {getStatusBadge(selectedTradie.status)}
                  {getSubscriptionBadge(selectedTradie)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedTradie.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedTradie.phone}</p>
                  </div>
                  {selectedTradie.website && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Website</p>
                      <a href={selectedTradie.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                        {selectedTradie.website}
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Free Subscription Toggle */}
                {selectedTradie.status === 'approved' && (
                  <div className="flex items-center justify-between p-4 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-3">
                      <Gift className="w-5 h-5 text-purple-600" />
                      <div>
                        <Label htmlFor="free-toggle" className="font-medium">Free Subscription</Label>
                        <p className="text-sm text-muted-foreground">Listing visible without payment</p>
                      </div>
                    </div>
                    <Switch
                      id="free-toggle"
                      checked={selectedTradie.is_free || false}
                      onCheckedChange={() => handleToggleFree(selectedTradie.id, selectedTradie.is_free)}
                      disabled={actionLoading}
                    />
                  </div>
                )}
                
                {/* Trial Info */}
                {selectedTradie.trial_ends_at && !selectedTradie.is_free && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Trial Period</p>
                      <p className="text-sm text-blue-700">
                        Ends: {new Date(selectedTradie.trial_ends_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
                
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Description</p>
                  <p className="text-sm bg-stone-50 p-3 rounded-lg">{selectedTradie.description}</p>
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Service Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTradie.categories.map(cat => (
                      <Badge key={cat} variant="secondary">{cat}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Service Regions</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTradie.regions.map(reg => (
                      <Badge key={reg} variant="outline">{reg}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              {selectedTradie.status === 'pending' && (
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setSelectedTradie(null)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleReject(selectedTradie.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                    Reject
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleApprove(selectedTradie.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                    Approve
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Detail Dialog */}
      <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <DialogContent>
          {selectedEnquiry && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Enquiry from {selectedEnquiry.name}</DialogTitle>
                <DialogDescription>
                  Sent to: {selectedEnquiry.tradie_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedEnquiry.email}</p>
                  </div>
                  {selectedEnquiry.phone && (
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedEnquiry.phone}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Message</p>
                  <p className="text-sm bg-stone-50 p-3 rounded-lg whitespace-pre-wrap">{selectedEnquiry.message}</p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Received: {new Date(selectedEnquiry.created_at).toLocaleString()}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tradie</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirm?.business_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
